import React from "react";
import { runGlobalSearch, type SearchHit, type SearchScope } from "../search/globalSearch";
import type { NavKey } from "../Sidebar";
import { Modal } from "./Modal";

type Props = {
  onNavigate: (key: NavKey, query?: string) => void;
};

const SCOPE_OPTIONS: { key: SearchScope; label: string }[] = [
  { key: "contracts", label: "合約" },
  { key: "legacyContracts", label: "舊合約" },
  { key: "cases", label: "案件" },
  { key: "disputes", label: "爭議" },
  { key: "brandAssets", label: "品牌資產" },
  { key: "lawyers", label: "顧問律師" },
  { key: "governance", label: "公司治理" },
  { key: "templates", label: "範本" },
  { key: "govInbound", label: "公文" },
  { key: "todos", label: "待辦" },
  { key: "approvalRequests", label: "合約審查" },
];

function getDefaultScopeState(): Record<SearchScope, boolean> {
  const state = {} as Record<SearchScope, boolean>;
  for (const it of SCOPE_OPTIONS) state[it.key] = true;
  return state;
}

function toActiveScopes(scopeState: Record<SearchScope, boolean>): SearchScope[] {
  return Object.entries(scopeState)
    .filter(([, v]) => !!v)
    .map(([k]) => k as SearchScope);
}

export function GlobalSearchBar({ onNavigate }: Props) {
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<SearchHit[]>([]);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  // Advanced Filter State
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [scopeState, setScopeState] = React.useState<Record<SearchScope, boolean>>(getDefaultScopeState());

  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const reqSeq = React.useRef(0);

  const activeScopes = React.useMemo(() => toActiveScopes(scopeState), [scopeState]);

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const q = query.trim();
      if (!q) {
        setResults([]);
        setShowDropdown(false);
        return;
      }

      const mySeq = ++reqSeq.current;

      // If user unchecked all scopes: show dropdown with empty results (message)
      if (activeScopes.length === 0) {
        setResults([]);
        setShowDropdown(true);
        return;
      }

      runGlobalSearch(q, dateFrom, dateTo, { scopes: activeScopes })
        .then((hits) => {
          if (mySeq !== reqSeq.current) return;
          setResults(hits);
          setShowDropdown(true);
        })
        .catch((e) => {
          console.error("[GlobalSearch] failed", e);
          if (mySeq !== reqSeq.current) return;
          setResults([]);
          setShowDropdown(false);
        });
    }, 200);

    return () => clearTimeout(timer);
  }, [query, dateFrom, dateTo, activeScopes]);

  // Click outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (hit: SearchHit) => {
    setQuery("");
    setShowDropdown(false);
    onNavigate(hit.navKey, hit.sourceId);
  };

  const applyAdvancedSearch = () => {
    const q = query.trim();
    if (!q) {
      setShowAdvanced(false);
      return;
    }

    const mySeq = ++reqSeq.current;

    if (activeScopes.length === 0) {
      setResults([]);
      setShowDropdown(true);
      setShowAdvanced(false);
      return;
    }

    runGlobalSearch(q, dateFrom, dateTo, { scopes: activeScopes })
      .then((hits) => {
        if (mySeq !== reqSeq.current) return;
        setResults(hits);
        setShowDropdown(true);
      })
      .catch((e) => {
        console.error("[GlobalSearch] failed", e);
        if (mySeq !== reqSeq.current) return;
        setResults([]);
        setShowDropdown(false);
      });

    setShowAdvanced(false);
  };

  const toggleScope = (key: SearchScope, checked: boolean) => {
    setScopeState((prev) => ({ ...prev, [key]: checked }));
  };

  const selectAllScopes = () => setScopeState(getDefaultScopeState());

  const clearAllScopes = () => {
    const cleared = {} as Record<SearchScope, boolean>;
    for (const it of SCOPE_OPTIONS) cleared[it.key] = false;
    setScopeState(cleared);
  };

  return (
    <div style={{ position: "relative", width: "100%", maxWidth: "720px", margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: "#0b0b0c",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "0 12px",
          transition: "border-color 0.2s",
        }}
      >
        <span style={{ fontSize: 16, marginRight: 10 }}>🔍</span>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="搜尋合約、案件、條款... (按 '/' 聚焦)"
          style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            padding: "12px 0",
            width: "100%",
            outline: "none",
            fontSize: 15,
          }}
          onFocus={() => {
            if (query) setShowDropdown(true);
          }}
        />
        <button
          onClick={() => setShowAdvanced(true)}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--muted)",
            cursor: "pointer",
            fontSize: 13,
            whiteSpace: "nowrap",
            paddingLeft: 12,
            borderLeft: "1px solid #333",
          }}
        >
          進階
        </button>
      </div>

      {showDropdown && query.trim() && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            background: "#1a1d24",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            zIndex: 1000,
            maxHeight: 400,
            overflowY: "auto",
          }}
        >
          {activeScopes.length === 0 ? (
            <div style={{ padding: "14px", color: "var(--muted)", fontSize: 13 }}>
              你把「資料範圍」全取消了，所以不會有結果。請至少勾選一個範圍。
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: "14px", color: "var(--muted)", fontSize: 13 }}>
              無符合結果（可試著用空白分隔多關鍵字，例如：<span style={{ color: "#e5e7eb" }}>統一 供應商</span>）
            </div>
          ) : (
            results.map((hit) => (
              <div
                key={hit.id}
                onClick={() => handleSelect(hit)}
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid #333",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#2a2d36")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <span className="badge" style={{ fontSize: 11, minWidth: 64, textAlign: "center" }}>
                  {hit.type}
                </span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: "bold", color: "#fff" }}>{hit.title}</div>
                  {hit.subtitle && <div style={{ fontSize: 12, color: "var(--muted)" }}>{hit.subtitle}</div>}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showAdvanced && (
        <Modal title="進階搜尋" onClose={() => setShowAdvanced(false)}>
          <div className="p" style={{ marginBottom: 10 }}>
            資料範圍：
          </div>

          <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            <button className="btn" style={{ width: "auto" }} onClick={selectAllScopes}>
              全選
            </button>
            <button className="btn" style={{ width: "auto" }} onClick={clearAllScopes}>
              全不選
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {SCOPE_OPTIONS.map((it) => (
              <label key={it.key} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!scopeState[it.key]}
                  onChange={(e) => toggleScope(it.key, e.target.checked)}
                />
                {it.label}
              </label>
            ))}
          </div>

          <div className="p" style={{ marginTop: 18, marginBottom: 10 }}>
            日期範圍 (建立日/簽約日/發生日)：
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="date" className="input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <span style={{ color: "var(--muted)" }}>~</span>
            <input type="date" className="input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          <div className="row" style={{ justifyContent: "flex-end", marginTop: 18 }}>
            <button className="btn" style={{ width: "auto" }} onClick={applyAdvancedSearch}>
              確認篩選
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
